import React, { useState } from "react";
import { MapPin, Mail, Send, Facebook, Twitter, Instagram } from "lucide-react";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create email content
    const emailSubject = formData.subject || "Contact Form Submission";
    const emailBody = `
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone || "Not provided"}
Subject: ${formData.subject}

Message:
${formData.message}
    `.trim();
    
    // Create mailto link
    const mailtoLink = `mailto:Oshokealasa.foundation@gmail.com?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    // Show confirmation and reset form
    alert("Thank you for your message! Your email client will open to send the message.");
    setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Our Location",
      details: [{ text: "No.1, Olaniran Olanyinka Street, Behind NNPC Filling Station, Prince and Princess Junction, Abuja" }],
    },
        {
      icon: Mail,
      title: "Email Address",
      details: [{ text: "Oshokealasa.foundation@gmail.com", href: "mailto:Oshokealasa.foundation@gmail.com" }],
    },
    {
      icon: Instagram,
      title: "Instagram",
      details: [{ text: "@oshokealasafdn", href: "https://www.instagram.com/oshokealasafdn/", external: true }],
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-cream py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-primary font-medium text-sm tracking-wider uppercase mb-3">GET IN TOUCH</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Contact Us
            </h1>
            <p className="text-gray-600 text-lg">
              Have questions or want to get involved? We'd love to hear from you. Reach out to us through any of the channels below.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <div key={index} className="bg-cream rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <info.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{info.title}</h3>
                {info.details.map((detail, i) => (
                  detail.href ? (
                    <a 
                      key={i} 
                      href={detail.href}
                      target={detail.external ? "_blank" : undefined}
                      rel={detail.external ? "noopener noreferrer" : undefined}
                      className="text-gray-600 hover:text-primary text-sm block transition-colors"
                    >
                      {detail.text}
                    </a>
                  ) : (
                    <p key={i} className="text-gray-600 text-sm">{detail.text}</p>
                  )
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <div className="bg-cream rounded-2xl p-8 md:p-12">
              <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="+234 XXX XXX XXXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="volunteer">Become a Volunteer</option>
                      <option value="donate">Donation Inquiry</option>
                      <option value="partnership">Partnership</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    placeholder="How can we help you?"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-medium text-sm transition-colors inline-flex items-center justify-center gap-2"
                >
                  SEND MESSAGE <Send className="w-4 h-4" />
                </button>
              </form>
            </div>

            {/* Info & Social */}
            <div className="flex flex-col justify-between">
              <div>
                <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Let's Work Together</h2>
                <p className="text-gray-600 leading-relaxed mb-8">
                  Whether you want to volunteer, partner with us, or simply learn more about our work, we're here to connect. Your support helps us continue making a difference in the lives of those who need it most.
                </p>
                
                <div className="bg-primary rounded-2xl p-8 text-white mb-8">
                  <h3 className="font-semibold text-xl mb-4">Ways to Get Involved</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-white rounded-full mt-2"></span>
                      <span>Volunteer for our outreach programs</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-white rounded-full mt-2"></span>
                      <span>Make a donation to support our causes</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-white rounded-full mt-2"></span>
                      <span>Partner with us on community projects</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-white rounded-full mt-2"></span>
                      <span>Spread the word about our mission</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  <a href="https://web.facebook.com/people/Oshoke-Alasa-Fdn/61581272517600/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 hover:bg-primary hover:text-white rounded-full flex items-center justify-center transition-colors">
                    <Facebook className="w-5 h-5" />
                  </a>
                  <a href="https://x.com/OshokeAlasaFdn" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 hover:bg-primary hover:text-white rounded-full flex items-center justify-center transition-colors">
                    <Twitter className="w-5 h-5" />
                  </a>
                  <a href="https://www.instagram.com/oshokealasafdn/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 hover:bg-primary hover:text-white rounded-full flex items-center justify-center transition-colors">
                    <Instagram className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="h-96 bg-gray-200 relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4">
            <MapPin className="w-12 h-12 text-primary mx-auto mb-4" />
            <p className="text-gray-600 max-w-md">No.1, Olaniran Olanyinka Street, Behind NNPC Filling Station, Prince and Princess Junction, Abuja</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
